import { getReservationApiUrl, getVehicleApiUrl } from '../../../../../config';
import type { ApiResponseMeta, ApiResponseStatus } from '../../domain/api';

import { getCurrToken } from '@/auth/application/handleAuth';
import { handleAxiosError } from '@/common/application/handleAxiosError';
import { logException } from '@/common/utils/logger';
import type { ApiVersionsObj } from '@/user/presentation/UserProfileScreen/UserProfileScreen/useUserProfileScreen';

// Define a type for the simplified response
export interface ApiResponse<T> {
    data: T;
    meta?: ApiResponseMeta;
    status: ApiResponseStatus;
}

export enum Target {
    Vehicle = 'vehicle',
    Reservation = 'reservation',
    User = 'user',
}
export type ResponseType =
    | 'arraybuffer'
    | 'blob'
    | 'document'
    | 'json'
    | 'text'
    | 'stream';

const useParkstoryApi = () => {

    const scope = (target: Target) => {
        switch (target) {
            case Target.Vehicle:
                return 'parkhouse';
            case Target.Reservation:
                return 'reservation';
            case Target.User:
                return 'parkhouse';
            default:
                return '';
        }
    };

    const _headers = async (target: Target) => {
        const newToken = await getCurrToken(); // retreives firebase token based on fb auth

        return ({
            'Accept': 'application/json',
            'Authorization': `Bearer ${newToken}`,
            'Content-Type': 'application/json',
            'scope': scope(target),
        });
    };
    const _mediaHeaders = async (target: Target) => {
        const newToken = await getCurrToken(); // retreives firebase token based on fb auth
        return ({
            'Accept': 'application/json',
            'Authorization': `Bearer ${newToken}`,
            'Content-Type': 'multipart/form-data',
            'scope': scope(target),
        });
    };


    const apiUrl = (target: Target): string => {
        switch (target) {
            case Target.Vehicle:
                return getVehicleApiUrl();
            case Target.Reservation:
                return getReservationApiUrl();
            case Target.User:
                return getVehicleApiUrl();
        }
    };

    const url = (target: Target, endpoint: string) => {
        const api = apiUrl(target);
        return new URL(`${api}${endpoint}`).toString();
    };


    const getApiVersions = async (target?: Target): Promise<ApiVersionsObj> => {
        const fetchVersion = async (target: Target): Promise<string> => {
            try {
                const headers = await _headers(target);
                const response = await fetch(apiUrl(target) + 'version', { headers, method: 'GET' });
                if (!response.ok) {
                    throw new Error(`Failed to fetch API version: ${response.statusText}`);
                }
                const data = await response.json();
                return data.version;
            } catch (error: any) {
                logException(error, { target });
                return '';
            }
        };
    
        try {
            if (target) {
                // If a specific target is provided, fetch only that version
                const version = await fetchVersion(target);
                return { [target]: version };
            } else {
                // Fetch all versions when target is not specified
                const [reservationVersion, vehicleVersion, userVersion] = await Promise.all([
                    fetchVersion(Target.Reservation),
                    fetchVersion(Target.Vehicle),
                    fetchVersion(Target.User),
                ]);
    
                return {
                    identity: userVersion ?? 'undefined',
                    parking: vehicleVersion ?? 'undefined',
                    reservation: reservationVersion ?? 'undefined'
                };
            }
        } catch (error: any) {
            throw handleAxiosError(error, 'getVersions');
        }
    };
    

    const makeRequest = async <T, U>(
        target: Target,
        endpoint: string,
        method: string,
        optionnal?:{
            data?: unknown,
            convertData?: (data: U) => T, // Conversion method
            overrideHeaders?: any,
            responseType?: ResponseType
        }
    ): Promise<ApiResponse<T>> => {
        const headers: Record<string, string> = optionnal?.overrideHeaders
            ? await _mediaHeaders(target)
            : await _headers(target);

        const fetchOptions: RequestInit = { headers, method };

        if (optionnal?.data && (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE')) {
            const data = optionnal.data;

            if (data instanceof FormData || data instanceof URLSearchParams) {
                fetchOptions.body = data;
                delete headers['Content-Type'];
            } else if (data instanceof Blob || data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
                fetchOptions.body = data as BodyInit;
                headers['Content-Type'] = (data instanceof Blob && data.type) ? data.type : 'application/octet-stream';
            } else if (typeof data === 'string') {
                fetchOptions.body = data;
                headers['Content-Type'] = 'text/plain';
            } else {
                fetchOptions.body = JSON.stringify(data);
                headers['Content-Type'] = 'application/json';
            }
        }

        try {
            const response = await fetch(url(target, endpoint), fetchOptions);
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const error: any = new Error(`HTTP ${response.status}: ${response.statusText}`);
                error.response = {
                    status: response.status,
                    data: errorData
                };
                throw error;
            }

            let responseBody: any;
            const contentType = response.headers.get('content-type');
            
            if (contentType?.includes('application/json')) {
                responseBody = await response.json();
            } else if (optionnal?.responseType === 'blob') {
                responseBody = await response.blob();
            } else if (optionnal?.responseType === 'arraybuffer') {
                responseBody = await response.arrayBuffer();
            } else if (optionnal?.responseType === 'text') {
                responseBody = await response.text();
            } else if (response.status !== 204) {
                responseBody = await response.json();
            } else {
                responseBody = {}
            }

            const status: ApiResponseStatus = {
                code: response.status,
                text: response.statusText
            };

            let responseData: U;
            if (Array.isArray(responseBody)) {
                // If responseBody is already an array, use it directly
                responseData = responseBody as U;
            } else if (responseBody?.data === undefined) {
                // Otherwise, use the responseBody as is
                responseData = responseBody as U;
            } else {
                // If responseBody.data is defined, use it
                responseData = responseBody.data as U;
            }

            const convertedData: T = optionnal?.convertData ? optionnal.convertData(responseData) : (responseData as unknown as T);

            const simplifiedResponse: ApiResponse<T> = {
                data: convertedData,
                meta: responseBody?.meta,
                status,
            };
            return simplifiedResponse;
        } catch (error) {
            logException(error, { endpoint, method });
            throw error;
        }
    };

    /*
     *     const buildReqTypeStr = (method:string, endpoint: string) => {
     *      const reqTypeStr = `${method}.${endpoint}`;
     *      if (Object.values(RequestTypeEnum).includes(reqTypeStr as RequestTypeEnum)) {
     *          return reqTypeStr as RequestTypeEnum;
     *      } // or handle the error as needed
     *      return RequestTypeEnum.error;
     *     }; 
     */
    const get = async <T>(target: Target, endpoint: string, responseType?: ResponseType): Promise<ApiResponse<T>> => makeRequest<T, unknown>(target, endpoint, 'GET', { responseType: responseType ?? 'json'});
    const post = async <T>(target: Target, endpoint: string, data: unknown): Promise<ApiResponse<T>> => makeRequest<T, unknown>(target, endpoint, 'POST', {data: { data: data }});
    const put = async <T>(target: Target, endpoint: string, data: any): Promise<ApiResponse<T>> => {
        // data needs to be wrapped for updating user profile in Identity Management 
        let wrappedData = { data };
        if (target === Target.User) {
            wrappedData = data;
        }
        return makeRequest<T, unknown>(target, endpoint, 'PUT', { data: wrappedData});
    };
    const remove = async <T>(target: Target, endpoint: string, data?: unknown): Promise<ApiResponse<T>> => makeRequest<T, unknown>(target, endpoint, 'DELETE', data !== undefined ? { data: { data } } : undefined);
    const postMedia = async <T>(target: Target, endpoint: string, data: any, headers?: any, responseType?: ResponseType): Promise<ApiResponse<T>> => makeRequest<T, unknown>(target, endpoint, 'POST', { data: data, convertData: (data: any) => data, overrideHeaders: headers, responseType: responseType ?? 'json'});

    // Modify the signature to accept a conversion function that operates on the data
    const convertApiResponse = <T, U>(response: ApiResponse<U>, convertData?: (data: U) => T): ApiResponse<T> => {
        try {
            let convertedData = response.data as unknown as T;
            if (typeof convertData === 'function') {
                convertedData = convertData(response.data);
            }

            // Apply the conversion function if provided, otherwise, use the original data

            // Return the entire response with the converted data
            return { ...response, data: convertedData };
        } catch (error) {
            logException(error);
            return {...response, data: null as T, status: { code: 400, text: 'Error in convert' } };
        }
    };

    return {
        convertApiResponse,
        get,
        getApiVersions,
        post,
        postMedia,
        put,
        remove
    };
};

export default useParkstoryApi;
